/** Keep in sync with lewouhmart-backend/src/settings/landing-sections.ts (DEFAULT_LANDING_SECTIONS). */

export type FeatureAccent =
  | 'teal'
  | 'purple'
  | 'amber'
  | 'rose'
  | 'emerald'
  | 'orange';

export type FeatureGridItem = {
  icon: string;
  title: string;
  body: string;
  accent: FeatureAccent;
};

export type SplitMediaSection = {
  id: string;
  type: 'SPLIT_MEDIA';
  enabled?: boolean;
  sortOrder?: number;
  kicker?: string;
  title: string;
  body: string;
  imageUrl: string;
  imageAlt?: string;
  imagePosition?: 'left' | 'right';
  ctaLabel?: string;
  ctaHref?: string;
  background?: 'default' | 'muted' | 'warm';
};

export type FeatureGridSection = {
  id: string;
  type: 'FEATURE_GRID';
  enabled?: boolean;
  sortOrder?: number;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  columns?: 2 | 3 | 4;
  items: FeatureGridItem[];
};

export type CardRowItem = {
  title: string;
  imageUrl: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

export type CardRowSection = {
  id: string;
  type: 'CARD_ROW';
  enabled?: boolean;
  sortOrder?: number;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  items: CardRowItem[];
};

export type EditorialEntry = {
  id?: string;
  date: string;
  title: string;
  imageUrl?: string;
  href?: string;
};

export type EditorialListSection = {
  id: string;
  type: 'EDITORIAL_LIST';
  enabled?: boolean;
  sortOrder?: number;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  featured: EditorialEntry & { body?: string; imageUrl: string };
  entries: EditorialEntry[];
};

export type ProductSpotlightSection = {
  id: string;
  type: 'PRODUCT_SPOTLIGHT';
  enabled?: boolean;
  sortOrder?: number;
  eyebrow?: string;
  title: string;
  subtitle?: string;
};

export type LandingSection =
  | SplitMediaSection
  | FeatureGridSection
  | CardRowSection
  | EditorialListSection
  | ProductSpotlightSection;

export const DEFAULT_LANDING_SECTIONS: LandingSection[] = [
  {
    id: 'chef-kitchen-story',
    type: 'SPLIT_MEDIA',
    enabled: true,
    sortOrder: 0,
    kicker: 'Our commitment',
    title: 'Quality sourcing, accurate inventory, dependable delivery',
    body: 'We combine careful supplier relationships with live stock tracking and fulfillment workflows—so what you order is what we prepare to ship. Teams monitor lots, availability, and service levels in one operational platform.',
    imageUrl:
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=80',
    imageAlt: 'Warehouse shelves with stocked goods',
    imagePosition: 'right',
    background: 'warm',
    ctaLabel: 'Browse the catalog',
    ctaHref: '/products',
  },
  {
    id: 'service-offering-grid',
    type: 'FEATURE_GRID',
    enabled: true,
    sortOrder: 1,
    eyebrow: 'Why customers choose us',
    title: 'Service offering',
    subtitle:
      'Shipping, speed, and support designed for households and businesses alike.',
    columns: 3,
    items: [
      {
        icon: 'fa-truck',
        title: 'Free shipping',
        body: 'On qualifying orders in covered delivery zones—no guesswork at checkout.',
        accent: 'teal',
      },
      {
        icon: 'fa-plane',
        title: 'Fast delivery',
        body: 'Optimized routing and status updates so you know when your shipment is on the way.',
        accent: 'purple',
      },
      {
        icon: 'fa-lock',
        title: 'Secure payment',
        body: 'Card data stays with certified processors; we only keep what reconciliation needs.',
        accent: 'amber',
      },
      {
        icon: 'fa-circle-question',
        title: 'Fast help',
        body: 'Human answers for swaps, substitutions, and order issues—without the runaround.',
        accent: 'rose',
      },
      {
        icon: 'fa-rotate-left',
        title: 'Fair returns',
        body: 'Clear policies when something arrives short or not as expected.',
        accent: 'emerald',
      },
      {
        icon: 'fa-thumbs-up',
        title: 'Consistent quality',
        body: 'We monitor fulfillment and inventory so you get a reliable experience order after order.',
        accent: 'orange',
      },
    ],
  },
  {
    id: 'spotlight-ingredients',
    type: 'CARD_ROW',
    enabled: true,
    sortOrder: 2,
    eyebrow: 'Popular picks',
    title: 'Trending in the catalog',
    subtitle: 'A snapshot of categories and listings shoppers explore most often.',
    items: [
      {
        title: 'Household essentials',
        imageUrl:
          'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&w=900&q=80',
        imageAlt: 'Retail shelves with everyday products',
        ctaLabel: 'Shop now',
        ctaHref: '/products',
      },
      {
        title: 'Seasonal highlights',
        imageUrl:
          'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80',
        imageAlt: 'Fresh produce at a market display',
        ctaLabel: 'Shop now',
        ctaHref: '/products',
      },
      {
        title: 'Staff picks',
        imageUrl:
          'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80',
        imageAlt: 'Warehouse packing and shipping area',
        ctaLabel: 'Shop now',
        ctaHref: '/products',
      },
    ],
  },
  {
    id: 'kitchen-journal',
    type: 'PRODUCT_SPOTLIGHT',
    enabled: true,
    sortOrder: 3,
    eyebrow: 'Store highlights',
    title: 'Featured products',
    subtitle:
      'Live listings from our catalog—refreshed as stock and new arrivals change. When you browse all departments, we mix picks across the store.',
  },
];
